import * as React from 'react';

import { IconArrowLeft, IconArrowRight } from '@xing-com/icons';
import { spaceS, spaceM, spaceXL } from '@xing-com/tokens';

import * as Styled from './carousel.styles';
import type { CarouselProps } from './carousel.types';
import { useWindowSize } from './hooks/use-window-size';
import { Item } from './item';

export const Carousel = React.forwardRef<HTMLDivElement, CarouselProps>(
  (
    {
      children,
      className,
      customScrollWidth,
      defaultActive,
      hidePageButtons,
      hideScrollbar,
      horizontalPadding,
      horizontalPaddingConfined,
      horizontalPaddingWide,
      itemMaxWidth,
      itemMinWidth,
      itemSnapPosition,
      itemSpacing = spaceM,
      itemSpacingConfined = spaceXL,
      itemSpacingWide,
      itemWidth = '80%',
      itemWidthConfined = '33%',
      itemWidthWide,
      onItemEnter,
      onNextClick,
      onPreviousClick,
      paddingBottom = spaceS,
      paddingBottomConfined,
      paddingBottomWide,
      paddingTop,
      paddingTopConfined,
      paddingTopWide,
      tabbable = false,
      verticalAlignment = 'stretch',
      ...props
    },
    forwardedRef
  ): JSX.Element => {
    const size = useWindowSize();
    const carouselRef = React.useRef<HTMLDivElement>();
    const [carouselDimensions, setCarouselDimension] = React.useState({
      clientWidth: 0,
      scrollWidth: 0,
      scrollLeft: 0,
    });
    const itemProps = {
      horizontalPadding,
      horizontalPaddingConfined,
      horizontalPaddingWide,
      itemMaxWidth,
      itemMinWidth,
      itemSnapPosition,
      itemSpacing,
      itemSpacingConfined,
      itemSpacingWide,
      itemWidth,
      itemWidthConfined,
      itemWidthWide,
      lastItemSpace: horizontalPadding,
      lastItemSpaceConfined: horizontalPaddingConfined,
      lastItemSpaceWide: horizontalPaddingWide,
    };

    const calculateSizes = (): void => {
      if (carouselRef.current) {
        setCarouselDimension({
          clientWidth: carouselRef.current.clientWidth,
          scrollWidth: carouselRef.current.scrollWidth,
          scrollLeft: carouselRef.current.scrollLeft,
        });
      }
    };

    const isCarouselStart = carouselDimensions.scrollLeft === 0;
    const isCarouselEnd =
      carouselDimensions.clientWidth +
        Math.ceil(carouselDimensions.scrollLeft) >=
      carouselDimensions.scrollWidth;

    const onHandlePreviousClick = (
      event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>
    ): void => {
      if (carouselRef.current) {
        onPreviousClick && onPreviousClick(event);
        carouselRef.current.scrollBy({
          top: 0,
          left: customScrollWidth
            ? -customScrollWidth
            : -carouselDimensions.clientWidth,
          behavior: 'smooth',
        });
      }
    };

    const onHandleNextClick = (
      event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>
    ): void => {
      if (carouselRef.current) {
        onNextClick && onNextClick(event);
        carouselRef.current.scrollBy({
          top: 0,
          left: customScrollWidth || carouselDimensions.clientWidth,
          behavior: 'smooth',
        });
      }
    };

    const handleScroll = (): void => {
      calculateSizes();
    };

    React.useEffect(() => {
      setTimeout(() => {
        calculateSizes();
      }, 500);
    }, [size]);

    React.useEffect(() => {
      if (!defaultActive) return;

      setTimeout(() => {
        const carouselChildrens = carouselRef.current?.children;
        const defaultActiveChildren = carouselChildrens?.[defaultActive];

        if (defaultActiveChildren) {
          defaultActiveChildren.scrollIntoView({ behavior: 'smooth' });
        }
      }, 500);
    }, [carouselRef, defaultActive]);

    return (
      <Styled.Container>
        <Styled.Carousel
          $hideScrollbar={hideScrollbar}
          $horizontalPadding={horizontalPadding}
          $horizontalPaddingConfined={horizontalPaddingConfined}
          $horizontalPaddingWide={horizontalPaddingWide}
          $paddingBottom={paddingBottom}
          $paddingBottomConfined={paddingBottomConfined}
          $paddingBottomWide={paddingBottomWide}
          $paddingTop={paddingTop}
          $paddingTopConfined={paddingTopConfined}
          $paddingTopWide={paddingTopWide}
          $verticalAlignment={verticalAlignment}
          className={className}
          data-xds="Carousel"
          onScroll={handleScroll}
          ref={forwardedRef || carouselRef}
          tabIndex={0}
          {...props}
        >
          {React.Children.map(children, (item, index) => (
            <Item
              itemIndex={index}
              key={index}
              tabbable={tabbable}
              onEnter={(direction: string | undefined, itemIndex: number) =>
                onItemEnter && onItemEnter({ direction, itemIndex })
              }
              {...itemProps}
            >
              {item}
            </Item>
          ))}
        </Styled.Carousel>
        {isCarouselStart || hidePageButtons ? null : (
          <Styled.PreviousPageButton
            $paddingBottom={paddingBottom || '0px'}
            $paddingBottomConfined={paddingBottomConfined || paddingBottom}
            $paddingBottomWide={paddingBottomWide || paddingBottom}
            $paddingTop={paddingTop || '0px'}
            $paddingTopConfined={paddingTopConfined || paddingTop}
            $paddingTopWide={paddingTopWide || paddingTop}
            aria-label="Previous Page"
            icon={IconArrowLeft}
            onClick={onHandlePreviousClick}
            size="medium"
            variant="tertiary"
          />
        )}
        {isCarouselEnd || hidePageButtons ? null : (
          <Styled.NextPageButton
            $paddingBottom={paddingBottom || '0px'}
            $paddingBottomConfined={paddingBottomConfined || paddingBottom}
            $paddingBottomWide={paddingBottomWide || paddingBottom}
            $paddingTop={paddingTop || '0px'}
            $paddingTopConfined={paddingTopConfined || paddingTop}
            $paddingTopWide={paddingTopWide || paddingTop}
            aria-label="Next Page"
            icon={IconArrowRight}
            onClick={onHandleNextClick}
            size="medium"
            variant="tertiary"
          />
        )}
      </Styled.Container>
    );
  }
);

Carousel.displayName = 'Carousel';
